import React from 'react';

/**
 * Escapes the four characters that let raw text become markup.
 *
 * This has to run BEFORE the markdown transforms below, never after: the
 * transforms insert real `<strong>`, `<code>` and `<a>` tags, and escaping
 * afterwards would turn those into visible text.
 *
 * The `"` is not optional. Without it, model or user text of the form
 * `https://x/"onmouseover="alert(1)` closes the `href` attribute that the
 * bare-URL transform opens, and injects an event handler without ever needing
 * an angle bracket.
 */
const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;')
   .replace(/</g, '&lt;')
   .replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;');

/**
 * Simple markdown-like renderer for AI chat messages.
 * Supports: bold, inline code, bullet/numbered lists, headings, and clickable URLs.
 * Shared between AIChatPanel (bubble) and AIChatView (full page).
 *
 * NOT AI-only: this also renders the Scope of Work on the PUBLIC proposal page
 * (PremiumScope) and in the printed PDF (ProposalPrintDocument). Anything
 * changed here changes a document a customer reads and signs, so
 * renderMarkdown.test.tsx pins the rendered output of realistic scope text.
 */
export function renderMarkdown(text: string): React.ReactNode[] {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // ── Headings ────────────────────────────────────────────────
    // These render as React TEXT CHILDREN, not through innerHTML, so React
    // does the escaping itself and the browser never decodes entities back.
    // They must therefore use the RAW line — escaping here would print a
    // literal "Demo &amp; Disposal" on the customer's proposal.
    // Checked before the transforms because a leading "## " is untouched by
    // them anyway, and reading the raw line keeps the intent obvious.
    if (line.startsWith('### ')) {
      return <h4 key={i} className="font-semibold tracking-tight text-sm mt-2 mb-1">{line.slice(4)}</h4>;
    }
    if (line.startsWith('## ')) {
      return <h3 key={i} className="font-semibold tracking-tight text-base mt-2 mb-1">{line.slice(3)}</h3>;
    }

    // ── Everything below reaches dangerouslySetInnerHTML ────────
    // Escape first, then add markup.
    let processed = escapeHtml(line);
    // Bold
    processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Inline code
    processed = processed.replace(/`(.*?)`/g, '<code class="bg-navy-850 text-acero-100 px-1.5 py-0.5 rounded-lg text-[12px] font-data">$1</code>');
    // Markdown links [text](url) — the scheme is restricted to http(s), so
    // `javascript:` and `data:` URLs never become anchors.
    processed = processed.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-navy-700 underline underline-offset-2 hover:text-navy-950 font-semibold transition-colors duration-150">$1</a>'
    );
    /* URLs sueltas — las que no están ya dentro de un enlace.
     *
     * Aquí había un `lookbehind` —`(?<!href="|">)`— y ROMPÍA SAFARI. No es que
     * se viera mal: Safari anterior a la 16.4 (marzo de 2023) no entiende esa
     * construcción y LANZA. Como toda la app cuelga del capturador de errores
     * de `index.tsx`, un iPhone viejo abriendo una propuesta con «Alcance del
     * trabajo» escrito no veía el documento: veía la pantalla de error. Y no
     * podía firmar.
     *
     * Se comprobó que el patrón viajaba en el bundle de producción.
     *
     * La versión de ahora hace lo mismo sin mirar hacia atrás: captura de forma
     * OPCIONAL lo que precede a la URL —el `href="` o el `">` que esta misma
     * función acaba de insertar en las dos transformaciones de arriba— y, si
     * viene, deja el texto tal cual. Sin ese prefijo, la URL está suelta y se
     * convierte en enlace.
     *
     * Sigue funcionando contra texto escapado por el mismo motivo que antes:
     * a estas alturas el usuario ya no puede tener una comilla suelta.
     */
    processed = processed.replace(
      /(href="|">)?(https?:\/\/[^\s<,)]+)/g,
      (todo, prefijo, url) => prefijo
        ? todo
        : `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-navy-700 underline underline-offset-2 hover:text-navy-950 font-semibold break-all transition-colors duration-150">${url}</a>`
    );
    // Bullet points
    if (processed.startsWith('- ') || processed.startsWith('• ')) {
      return (
        <div key={i} className="flex gap-2 ml-2 my-0.5">
          <span className="text-brand-500 mt-0.5" aria-hidden="true">•</span>
          <span dangerouslySetInnerHTML={{ __html: processed.slice(2) }} />
        </div>
      );
    }
    // Numbered list
    const numMatch = processed.match(/^(\d+)\.\s/);
    if (numMatch) {
      return (
        <div key={i} className="flex gap-2 ml-2 my-0.5">
          <span className="text-acero-500 font-data tabular-nums text-[12px] font-medium min-w-[20px] mt-[1px]">{numMatch[1]}.</span>
          <span dangerouslySetInnerHTML={{ __html: processed.slice(numMatch[0].length) }} />
        </div>
      );
    }
    // Empty line = spacing
    if (!processed.trim()) {
      return <div key={i} className="h-2" />;
    }
    // Regular paragraph
    return <p key={i} className="my-0.5" dangerouslySetInnerHTML={{ __html: processed }} />;
  });
}
